// A minimal ZIP archive reader/writer — hand-written rather than an added
// dependency, matching this app's existing "no external library" convention
// for well-understood, stable formats (see database.ts's own comment on the
// same reasoning for IndexedDB). Store method only (no DEFLATE compression):
// the payload here is either already-compressed/lossless audio (WAV/MP3,
// where re-compressing saves little) or a small JSON manifest, so the
// simplicity of skipping a compression codec entirely is worth the modest
// size cost. Supports exactly what this app needs — a flat list of named
// entries — not the full ZIP spec (no folders-as-entries, no encryption, no
// ZIP64 for archives over 4GB, no split archives).

const CRC_TABLE = buildCrcTable();

function buildCrcTable(): Uint32Array {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** MS-DOS date/time encoding ZIP entries use — fixed to a single arbitrary
 * moment rather than the real current time. This module can't call
 * Date.now() at all: it's also exercised by workflow-style tooling that
 * treats Date.now()/new Date() as unavailable so cached runs stay
 * deterministic, and there's no reason a backup archive's internal
 * timestamp needs to be real — the meaningful timestamp (`exportedAt`)
 * already lives in the JSON manifest inside the archive. */
const FIXED_DOS_TIME = 0;
const FIXED_DOS_DATE = (1 << 9) | (1 << 5) | 1; // 1980-01-01, the ZIP epoch

export interface ZipEntryInput {
  path: string;
  data: Uint8Array;
}

// Pinned to the ArrayBuffer-backed generic (not the looser ArrayBufferLike a
// bare `Uint8Array` allows) — readZip's own implementation always produces
// one via file.arrayBuffer().slice(...), and callers (see
// parseFullSetlistZip in store.ts) construct a Blob directly from this
// field, which requires the narrower type.
export interface ZipEntryOutput {
  path: string;
  data: Uint8Array<ArrayBuffer>;
}

function utf8Encode(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

function writeUint16LE(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

function writeUint32LE(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

/** Builds a ZIP archive (as a Blob) from a flat list of entries. Each
 * entry's path is written as a plain filename (forward slashes for any
 * subdirectory structure, per the ZIP spec's convention, not the host
 * platform's). */
export function createZip(entries: ZipEntryInput[]): Blob {
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = utf8Encode(entry.path);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    writeUint32LE(localView, 0, 0x04034b50); // local file header signature
    writeUint16LE(localView, 4, 20); // version needed to extract
    writeUint16LE(localView, 6, 0); // general purpose flag
    writeUint16LE(localView, 8, 0); // compression method: store
    writeUint16LE(localView, 10, FIXED_DOS_TIME);
    writeUint16LE(localView, 12, FIXED_DOS_DATE);
    writeUint32LE(localView, 14, crc);
    writeUint32LE(localView, 18, size); // compressed size
    writeUint32LE(localView, 22, size); // uncompressed size
    writeUint16LE(localView, 26, nameBytes.length);
    writeUint16LE(localView, 28, 0); // extra field length
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, entry.data);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32LE(centralView, 0, 0x02014b50); // central directory header signature
    writeUint16LE(centralView, 4, 20); // version made by
    writeUint16LE(centralView, 6, 20); // version needed to extract
    writeUint16LE(centralView, 8, 0); // general purpose flag
    writeUint16LE(centralView, 10, 0); // compression method: store
    writeUint16LE(centralView, 12, FIXED_DOS_TIME);
    writeUint16LE(centralView, 14, FIXED_DOS_DATE);
    writeUint32LE(centralView, 16, crc);
    writeUint32LE(centralView, 20, size);
    writeUint32LE(centralView, 24, size);
    writeUint16LE(centralView, 28, nameBytes.length);
    writeUint16LE(centralView, 30, 0); // extra field length
    writeUint16LE(centralView, 32, 0); // comment length
    writeUint16LE(centralView, 34, 0); // disk number start
    writeUint16LE(centralView, 36, 0); // internal file attributes
    writeUint32LE(centralView, 38, 0); // external file attributes
    writeUint32LE(centralView, 42, offset); // relative offset of local header
    centralHeader.set(nameBytes, 46);

    centralParts.push(centralHeader);
    offset += localHeader.length + entry.data.length;
  }

  const centralDirSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const centralDirOffset = offset;

  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  writeUint32LE(endView, 0, 0x06054b50); // end of central directory signature
  writeUint16LE(endView, 4, 0); // disk number
  writeUint16LE(endView, 6, 0); // disk with central directory
  writeUint16LE(endView, 8, entries.length); // entries on this disk
  writeUint16LE(endView, 10, entries.length); // total entries
  writeUint32LE(endView, 12, centralDirSize);
  writeUint32LE(endView, 16, centralDirOffset);
  writeUint16LE(endView, 20, 0); // comment length

  // Concatenated into one fresh, plain ArrayBuffer-backed Uint8Array before
  // handing off to Blob — entry.data may be a view over some other
  // ArrayBufferLike (this project's TS lib target treats Uint8Views as
  // potentially SharedArrayBuffer-backed, which Blob's constructor type
  // rejects even though nothing here ever actually uses one), and copying
  // once here sidesteps that entirely rather than fighting the type at
  // every call site that produces entry.data.
  const allParts = [...localParts, ...centralParts, endRecord];
  const totalLength = allParts.reduce((sum, part) => sum + part.length, 0);
  const combined = new Uint8Array(totalLength);
  let writeOffset = 0;
  for (const part of allParts) {
    combined.set(part, writeOffset);
    writeOffset += part.length;
  }

  return new Blob([combined], { type: 'application/zip' });
}

/** Reads a ZIP archive's entries back out, via the central directory (the
 * authoritative index at the end of the file) rather than scanning local
 * headers sequentially — the same approach every real ZIP reader uses,
 * since local headers can lie (their sizes may be 0 with a trailing data
 * descriptor instead, for a streamed write) while the central directory
 * always has the real values. Only supports the store (uncompressed)
 * method createZip itself produces — throws for anything else, rather
 * than silently returning corrupt data, since this app never needs to
 * read a ZIP it didn't write. */
export async function readZip(file: Blob): Promise<ZipEntryOutput[]> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);

  const endSignature = 0x06054b50;
  let endOffset = -1;
  for (let i = buffer.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === endSignature) {
      endOffset = i;
      break;
    }
  }
  if (endOffset === -1) throw new Error('Not a valid ZIP file (no end-of-central-directory record found).');

  const totalEntries = view.getUint16(endOffset + 10, true);
  const centralDirOffset = view.getUint32(endOffset + 16, true);

  const entries: ZipEntryOutput[] = [];
  let cursor = centralDirOffset;
  for (let i = 0; i < totalEntries; i++) {
    const signature = view.getUint32(cursor, true);
    if (signature !== 0x02014b50) throw new Error('Corrupt ZIP central directory.');
    const compressionMethod = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const nameBytes = buffer.subarray(cursor + 46, cursor + 46 + nameLength);
    const path = new TextDecoder().decode(nameBytes);

    if (compressionMethod !== 0) {
      throw new Error(`Unsupported ZIP compression method for "${path}" — only uncompressed (store) entries are supported.`);
    }

    const localNameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + localNameLength + localExtraLength;
    const data = buffer.slice(dataStart, dataStart + compressedSize);

    entries.push({ path, data });
    cursor += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}
